import React from 'react';
import styled, { withTheme } from 'styled-components';
import ModalContainer from '../../../atoms/Modal/ModalContainer';
import Button from '../../../atoms/Button/Button';
import Flex from '../../../atoms/Box/Flex';
import { formatDate, formatTimeSeconds } from '../../../../services/helpers/dateTimeFormats';
import TwoRowCell from '../../../atoms/Table/TwoRowCell';
import { FailedWebhook } from '../../../../types/webhooks/FailedWebhook';
import { decodeWebhookType } from './FailedWebhooksSearchResults';
import { useWebhooksStore } from '../../../../store/admin/webhooks/webhooksStore';

interface LogDetailsProps {
    failedWebhook: FailedWebhook;
    hide: () => void;
}

const FailedWebhookDetails = ({ failedWebhook, hide }: LogDetailsProps) => {
    const { setBulkAction } = useWebhooksStore();

    const date = formatDate(new Date(failedWebhook.action.timestamp * 1000));
    const time = formatTimeSeconds(new Date(failedWebhook.action.timestamp * 1000));

    const MessageDetails = ({ failedWebhookData }: { failedWebhookData: FailedWebhook }) => {
        const convertToFormattedJSON = (jsonString: string) => {
            try {
                return JSON.stringify(JSON.parse(jsonString), null, 2);
            } catch (error) {
                return JSON.stringify(null);
            }
        };

        return (
            <MessageDetailsOuter>
                <Flex gap="5rem">
                    <Flex gap="1rem" column>
                        <Bold>Authorization method</Bold>
                        <Bold>Type</Bold>
                        <Bold>Payload</Bold>
                    </Flex>
                    <Flex gap="1rem" column>
                        <div data-testid="failedWebhook-details-authorization-method">
                            {failedWebhookData.action.authorizationMethod}
                        </div>
                        <div data-testid="failedWebhook-details-type">
                            {decodeWebhookType(failedWebhookData.action.data)}
                        </div>
                        <JsonContainer data-testid="failedWebhook-details-payload">
                            {convertToFormattedJSON(failedWebhookData.action.data)}
                        </JsonContainer>
                    </Flex>
                </Flex>
            </MessageDetailsOuter>
        );
    };

    return (
        <ModalOuter data-testid="failedWebhook-details">
            <div>
                <ModalContainer position="header">
                    <Flex justifyContent="space-between">
                        <Flex gap="1rem">
                            <Button
                                onClick={() => {
                                    hide();
                                    setBulkAction({
                                        action: 'Resend',
                                        failedWebhooks: [failedWebhook],
                                    });
                                }}
                                data-testid="failedWebhook-details-resend"
                            >
                                Resend
                            </Button>
                            <Button
                                onClick={() => {
                                    hide();
                                    setBulkAction({
                                        action: 'Delete',
                                        failedWebhooks: [failedWebhook],
                                    });
                                }}
                                data-testid="failedWebhook-details-delete"
                            >
                                Delete
                            </Button>
                        </Flex>
                        <Button onClick={hide} data-testid="failedWebhook-details-close">
                            Close
                        </Button>
                    </Flex>
                </ModalContainer>
                <ModalContainerHeaderBottom>
                    <ModalContainer position="content">
                        <Flex gap="5rem">
                            <div>
                                <SmallTitle>Timestamp</SmallTitle>
                                <TwoRowCell upper={date.yyyyMMdd} lower={time} />
                            </div>
                            <div>
                                <SmallTitle>Method</SmallTitle>
                                <span data-testid="failedWebhook-details-method">
                                    {failedWebhook.action.method.method}
                                </span>
                            </div>
                            <div>
                                <SmallTitle>Webhook endpoint</SmallTitle>
                                <span data-testid="failedWebhook-details-endpoint">
                                    {failedWebhook.action.url}
                                </span>
                            </div>
                        </Flex>
                    </ModalContainer>
                </ModalContainerHeaderBottom>
            </div>
            <ModalContainerContent>
                <MessageDetails failedWebhookData={failedWebhook} />
            </ModalContainerContent>
        </ModalOuter>
    );
};

const MessageDetailsOuter = styled.div`
    max-height: calc(100vh - 40rem);
    overflow-y: scroll;
`;

const ModalOuter = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1.5rem;
    background-color: ${props => props.theme.colors.border};
    border-radius: 0.5rem;
    height: fit-content;
`;

const ModalContainerHeaderBottom = styled.div`
    background-color: ${props => props.theme.colors.light};
    border-radius: 0 0 0.5rem 0.5rem;
`;

const ModalContainerContent = styled.div`
    background-color: ${props => props.theme.colors.light};
    border-radius: 0.5rem;
    padding: ${props => props.theme.layout.padding.large};
`;

const SmallTitle = styled.div`
    margin-bottom: 1.5rem;
    font-size: ${props => props.theme.text.size.medium};
`;

const Bold = styled.div`
    font-family: ${props => props.theme.text.font.bold};
    white-space: nowrap;
`;

const JsonContainer = styled.div`
    white-space: pre-wrap;
    font-family: monospace;
    font-size: ${props => props.theme.text.size.xsmall};
    background-color: ${props => props.theme.colors.border};
    padding: 1rem;
    border-radius: 0.5rem;
`;

export default withTheme(FailedWebhookDetails) as React.ComponentType<
    Omit<LogDetailsProps, 'theme'>
>;
